import { useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { ShoppingCart, Check, ChevronRight, Download, Zap, ChevronLeft, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/lib/cart";
import { SEO } from "@/components/seo";
import { getSubscriptionAccessMessage } from "@/lib/subscription-copy";
import type { Product, Category } from "@shared/schema";

function ProductGallery({ product }: { product: Product }) {
  const allImages = [
    ...(product.image ? [product.image] : []),
    ...(product.images || []),
  ];
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const goNext = useCallback(() => {
    setSelectedIndex((prev) => (prev + 1) % allImages.length);
  }, [allImages.length]);

  const goPrev = useCallback(() => {
    setSelectedIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  }, [allImages.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "Escape") setLightboxOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, goNext, goPrev]);

  if (allImages.length === 0) {
    return (
      <div className="rounded-md border bg-muted flex items-center justify-center aspect-video">
        <span className="text-muted-foreground text-sm">No image available</span>
      </div>
    );
  }

  return (
    <div>
      <div
        className="relative rounded-md overflow-hidden border cursor-pointer group"
        onClick={() => setLightboxOpen(true)}
        data-testid="img-product-detail"
      >
        <img
          src={allImages[selectedIndex]}
          alt={`${product.name} - Image ${selectedIndex + 1}`}
          className="w-full aspect-video object-cover"
        />
        {allImages.length > 1 && (
          <>
            <Button
              size="icon"
              variant="ghost"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              data-testid="button-gallery-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              data-testid="button-gallery-next"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              {allImages.map((_, idx) => (
                <div
                  key={idx}
                  role="button"
                  className={`w-2 h-2 rounded-full transition-colors cursor-pointer ${idx === selectedIndex ? "bg-white" : "bg-white/40"}`}
                  onClick={(e) => { e.stopPropagation(); setSelectedIndex(idx); }}
                  data-testid={`button-gallery-dot-${idx}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {allImages.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          {allImages.map((img, idx) => (
            <div
              key={idx}
              role="button"
              className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-colors cursor-pointer ${idx === selectedIndex ? "border-primary" : "border-transparent opacity-60 hover:opacity-100"}`}
              onClick={() => setSelectedIndex(idx)}
              data-testid={`button-gallery-thumb-${idx}`}
            >
              <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
          data-testid="lightbox-overlay"
        >
          <Button
            size="icon"
            variant="ghost"
            className="absolute top-4 right-4 text-white z-10 rounded-full"
            onClick={() => setLightboxOpen(false)}
            data-testid="button-lightbox-close"
          >
            <X className="w-6 h-6" />
          </Button>
          <div className="relative flex items-center gap-4 max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            {allImages.length > 1 && (
              <Button
                size="icon"
                variant="ghost"
                className="flex-shrink-0 text-white rounded-full bg-white/10"
                onClick={goPrev}
                data-testid="button-lightbox-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>
            )}
            <div className="flex flex-col items-center">
              <img
                src={allImages[selectedIndex]}
                alt={`${product.name} - Image ${selectedIndex + 1}`}
                className="max-w-full max-h-[80vh] object-contain rounded-md"
                data-testid="img-lightbox"
              />
              {allImages.length > 1 && (
                <div className="text-center text-white/60 text-sm mt-3">
                  {selectedIndex + 1} / {allImages.length}
                </div>
              )}
            </div>
            {allImages.length > 1 && (
              <Button
                size="icon"
                variant="ghost"
                className="flex-shrink-0 text-white rounded-full bg-white/10"
                onClick={goNext}
                data-testid="button-lightbox-next"
              >
                <ChevronRight className="w-6 h-6" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { items, addItem } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const { data: product, isLoading } = useQuery<Product>({
    queryKey: ["/api/products", slug],
  });

  const { data: category } = useQuery<Category>({
    queryKey: ["/api/categories/by-id", product?.categoryId?.toString()],
    enabled: !!product?.categoryId,
  });

  const isInCart = product ? items.some((item) => item.product.id === product.id) : false;

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Skeleton className="h-4 w-64 mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Skeleton className="w-full aspect-video rounded-md" />
          <div>
            <Skeleton className="h-8 w-3/4 mb-4" />
            <Skeleton className="h-5 w-full mb-2" />
            <Skeleton className="h-5 w-2/3 mb-6" />
            <Skeleton className="h-10 w-40 mb-4" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h1 className="text-2xl font-bold mb-2">Product Not Found</h1>
        <p className="text-muted-foreground mb-4">
          The product you're looking for doesn't exist.
        </p>
        <Link href="/">
          <span className="text-primary cursor-pointer hover:underline">
            Back to Home
          </span>
        </Link>
      </div>
    );
  }

  const allProductImages = [
    ...(product.image ? [product.image] : []),
    ...(product.images || []),
  ];

  const productJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.description,
      image: allProductImages.length > 0 ? allProductImages : undefined,
      url: `https://sierrachartpro.com/product/${product.slug}`,
      brand: { "@type": "Brand", name: "Sierra Chart Pro" },
      category: category?.name || "Trading Tools",
      offers: {
        "@type": "Offer",
        price: Number(product.price).toFixed(2),
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        seller: { "@type": "Organization", name: "Sierra Chart Pro" },
        url: `https://sierrachartpro.com/product/${product.slug}`,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sierrachartpro.com/" },
        ...(category ? [{ "@type": "ListItem" as const, position: 2, name: category.name, item: `https://sierrachartpro.com/category/${category.slug}` }] : []),
        { "@type": "ListItem", position: category ? 3 : 2, name: product.name, item: `https://sierrachartpro.com/product/${product.slug}` },
      ],
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title={`${product.name} - Buy Sierra Chart ${category?.group === "indicators" ? "Indicator" : "Template"}`}
        description={`${product.description} Price: $${Number(product.price).toFixed(2)}. Instant digital download for Sierra Chart trading platform.`}
        keywords={`${product.name}, buy ${product.name}, ${product.name} Sierra Chart, ${product.name} download, ${category?.name || "trading"} indicator, Sierra Chart ${category?.group || "template"}`}
        ogType="product"
        ogImage={allProductImages[0] || undefined}
        jsonLd={productJsonLd}
      />
      <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-6 flex-wrap" data-testid="breadcrumb">
        <Link href="/">
          <span className="cursor-pointer hover:text-foreground transition-colors">Home</span>
        </Link>
        <ChevronRight className="w-3 h-3" />
        {category && (
          <>
            <Link href={`/category/${category.slug}`}>
              <span className="cursor-pointer hover:text-foreground transition-colors">
                {category.name}
              </span>
            </Link>
            <ChevronRight className="w-3 h-3" />
          </>
        )}
        <span className="text-foreground">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        <ProductGallery product={product} />


        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {product.isFeatured && (
              <Badge>
                <Zap className="w-3 h-3 mr-1" />
                Featured
              </Badge>
            )}
            {category && (
              <Badge variant="secondary">
                {category.name}
              </Badge>
            )}
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold mb-3" data-testid="text-product-detail-name">
            {product.name}
          </h1>

          <p className="text-muted-foreground leading-relaxed mb-6" data-testid="text-product-detail-desc">
            {product.description}
          </p>

          {product.features && product.features.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">
                Features
              </h3>
              <ul className="space-y-2">
                {product.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Separator className="mb-6" />

          {(() => {
            const saleType: string = (product as any).saleType ?? "purchase";
            const canPurchase = saleType === "purchase" || saleType === "both";
            const canSubscribe = saleType === "subscribe" || saleType === "both";
            const subscriptionAccessMessage = getSubscriptionAccessMessage(product.categoryId);
            return (
              <>
                {canPurchase && (
                  <div className="flex items-center gap-4 mb-6 flex-wrap">
                    <span className="text-3xl font-bold text-primary" data-testid="text-product-detail-price">
                      ${Number(product.price).toFixed(2)}
                    </span>
                    <Badge variant="secondary">
                      <Download className="w-3 h-3 mr-1" />
                      Digital Download
                    </Badge>
                  </div>
                )}

                {!canPurchase && canSubscribe && (
                  <div className="flex items-center gap-3 mb-6 flex-wrap">
                    <Badge variant="secondary">Subscription Required</Badge>
                  </div>
                )}

                {canPurchase && (
                  <>
                    <Button
                      size="lg"
                      className="w-full sm:w-auto"
                      onClick={() => addItem(product)}
                      disabled={isInCart}
                      variant={isInCart ? "secondary" : "default"}
                      data-testid="button-add-to-cart-detail"
                    >
                      {isInCart ? (
                        <><Check className="w-4 h-4 mr-2" />Added to Cart</>
                      ) : (
                        <><ShoppingCart className="w-4 h-4 mr-2" />Add to Cart</>
                      )}
                    </Button>
                    {isInCart && (
                      <div className="mt-3">
                        <Link href="/cart">
                          <span className="text-sm text-primary cursor-pointer hover:underline" data-testid="link-go-to-cart">
                            Go to Cart &rarr;
                          </span>
                        </Link>
                      </div>
                    )}
                  </>
                )}

                {canSubscribe && (
                  <div className={canPurchase ? "mt-3" : ""}>
                    <Link href="/pricing">
                      <Button
                        size="lg"
                        variant={canPurchase ? "outline" : "default"}
                        className="w-full sm:w-auto"
                        data-testid="button-subscribe-detail"
                      >
                        <Zap className="w-4 h-4 mr-2" />
                        {canPurchase ? "Or Subscribe for Access" : "Subscribe to Download"}
                      </Button>
                    </Link>
                    <p className="mt-3 w-fit rounded-md border border-amber-400/60 bg-amber-400/10 px-3 py-2 text-center text-sm font-semibold leading-snug text-amber-300 transition-all duration-200 hover:-translate-y-0.5 hover:border-amber-300 hover:bg-amber-400/20 hover:text-amber-200 hover:shadow-[0_4px_14px_rgba(251,191,36,0.2)]">
                      {subscriptionAccessMessage}
                    </p>
                  </div>
                )}
              </>
            );
          })()}

          <Card className="mt-8">
            <CardContent className="p-4">
              <h3 className="font-semibold text-sm mb-3">What you'll get:</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Download className="w-3 h-3 text-primary flex-shrink-0" />
                  Instant download after purchase
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-primary flex-shrink-0" />
                  Compatible with Sierra Chart
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-primary flex-shrink-0" />
                  Easy import instructions included
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
